import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { GraphTrendIconComponent } from '../../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { GroupedBarChartComponent } from '../../../../../../_shared/components/graphs/grouped-bar-chart/grouped-bar-chart.component';
import { calculateChartYTicks } from '../../../../../../_shared/helpers/chart-y-ticks.helper';
import { FilterService } from '../../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { MaintenanceCostingService } from '../../../services/maintenance-costing.service';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../../_shared/enums';
import { TimeSeriesGroupingHelper, GroupedChartData } from '../../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { SummaryRequest } from '../../../../../../_shared/models/summary-request.model';
import { hasCompleteDateRange, splitWidgetFilter } from '../../../../../../_shared/helpers/widget-filter.helper';
import { MaintenanceInsightsResponse } from '../../../../maintenance-dashboard/models/maintenance-dashboard-response.model';
import { combineKpiInsights } from '../../../helpers/kpi-insights.helper';
import { EmptyStateComponent } from '../../../../../../_shared/components/states/empty-state/empty-state.component';
import { MonthlyCostComparisonModalComponent } from '../../modals/monthly-cost-comparison-modal/monthly-cost-comparison-modal.component';
import { Subject } from 'rxjs';
import { WidgetFilterData } from '../../../../../../_shared/models/filters/widget-filter-data.model';
import { AuthService } from '../../../../../../_shared/services';
import { CurrencyHelper } from '../../../../../../_shared/helpers/currency/currency-map.helper';
import { formatValue } from '../../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-monthly-cost-comparison',
  standalone: true,
  imports: [CommonModule, GraphTrendIconComponent, GroupedBarChartComponent, EmptyStateComponent],
  templateUrl: './monthly-cost-comparison.component.html',
  styleUrl: './monthly-cost-comparison.component.scss'
})
export class MonthlyCostComparisonComponent implements OnInit, OnDestroy {
  private modalService = inject(NgbModal);
  private costingService = inject(MaintenanceCostingService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);
  private authService = inject(AuthService);
  private readonly transloco = inject(TranslocoService);

  chartData: GroupedChartData[] = [];
  readonly barWidth = 12;
  readonly chartHeight = 145;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  readonly subgroups = [
    { name: 'inHouse', legend: this.transloco.translate('maintenanceCosting.charts.monthlyCostComparison.legend.inHouse'), color: '#8D63C3' },
    { name: 'outsourced', legend: this.transloco.translate('maintenanceCosting.charts.monthlyCostComparison.legend.outsourced'), color: '#F4BC2B' }
  ];

  get title(): string {
    return this.transloco.translate('maintenanceCosting.charts.monthlyCostComparison.title');
  }

  isLoading: boolean = true;
  totalValue: number = 0;
  percentageChange: number = 0;
  widgetFilter: WidgetFilterData;
  currencySymbol: string = '';
  currencyCode: string = '';

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.filterService.filterUpdated$
      .pipe(takeUntilDestroyed())
      .subscribe(async (filter) => {
        if (!hasCompleteDateRange(filter)) return;

        this.widgetFilter = filter;
        await this.loadCostData();
      });
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);

    // The shared filter may still hold another page's selection
    if (hasCompleteDateRange(this.widgetFilter)) {
      this.loadCostData();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadCostData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: SummaryRequest = {
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        accountIds: this.widgetFilter.accountIds,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      const [inHouseResponse, outsourcedResponse] = await Promise.all([
        this.costingService.MonthlyInHouseCostKpi(request, this.cancelSubject),
        this.costingService.MonthlyOutsourceKpi(request, this.cancelSubject)
      ]);

      const inHouseData = inHouseResponse.data?.data ?? [];
      const outsourcedData = outsourcedResponse.data?.data ?? [];
      const summary = combineKpiInsights([inHouseResponse.data, outsourcedResponse.data]);

      this.chartData = this.processResponses(inHouseData, outsourcedData);
      this.yTicks = this.calculateYTicks(this.chartData);
      this.percentageChange = summary.percentageChange;
      this.totalValue = summary.totalValue;

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.chartData = [];
          this.isLoading = false;
        }
      });
    }
  }

  private processResponses(
    inHouseData: MaintenanceInsightsResponse[],
    outsourcedData: MaintenanceInsightsResponse[]
  ): GroupedChartData[] {
    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.widgetFilter.interval);

    const datasets = [
      { data: inHouseData, label: 'inHouse' },
      { data: outsourcedData, label: 'outsourced' }
    ];

    switch (groupingType) {
      case 'hourly': return TimeSeriesGroupingHelper.groupByHours(datasets);
      case 'daily': return TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!, 7);
      default: return TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' {
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    return 'weekly';
  }

  private calculateYTicks(chartData: GroupedChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues = chartData.flatMap(item => [
      Number(item['inHouse']) || 0,
      Number(item['outsourced']) || 0
    ]);

    return calculateChartYTicks(allValues, this.defaultYTicks);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false; // Cost increase is negative
  }

  formatValue(): string {
    return formatValue(this.totalValue, { currencySymbol: this.currencySymbol });
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      // Check all subgroups for non-zero values
      return Object.keys(item).some(key => {
        if (key === 'date' || key === 'originalTimestamp' || key === 'displayLabel' || key === 'tooltipDateTime') return false;
        const value = Number(item[key]) || 0;
        return value > 0;
      });
    });
  }

  openModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    const modalRef = this.modalService.open(MonthlyCostComparisonModalComponent, modalOptions);
    Object.assign(modalRef.componentInstance, splitWidgetFilter(this.filterService.filter));
  }
}